/**
 * NavRail — functional vertical icon rail (Phase 1B).
 *
 * Icons link to Founder Console, Projects, Conversations, Memory,
 * Decisions, Ideas, Tasks, Settings. The bottom Q button opens the
 * Qoryn Command Palette (also Cmd/Ctrl+K).
 *
 * Design language preserved from Phase 0: slim 64px rail, subtle border,
 * cyan accent for active route, understated hover.
 */

import { NavLink } from "react-router-dom";
import {
    LayoutGrid, FolderKanban, MessagesSquare, Brain,
    CheckCircle2, Lightbulb, ListTodo, Settings as SettingsIcon, Target,
} from "lucide-react";

const NAV_ITEMS = [
    { id: "console", to: "/",              icon: LayoutGrid,      label: "Founder Console" },
    { id: "projects", to: "/projects",     icon: FolderKanban,    label: "Projects" },
    { id: "conversations", to: "/conversations", icon: MessagesSquare, label: "Conversations" },
    { id: "memory", to: "/memory",         icon: Brain,           label: "Memory" },
    { id: "decisions", to: "/decisions",   icon: CheckCircle2,    label: "Decisions" },
    { id: "ideas", to: "/ideas",           icon: Lightbulb,       label: "Ideas" },
    { id: "tasks", to: "/tasks",           icon: ListTodo,        label: "Tasks" },
    { id: "gtm", to: "/gtm",               icon: Target,          label: "GTM Command Center" },
    { id: "settings", to: "/settings",     icon: SettingsIcon,    label: "Settings" },
];

function FooterQButton({ onClick }) {
    const modLabel =
        typeof navigator !== "undefined" &&
        /Mac|iPad|iPhone|iPod/i.test(navigator.platform || navigator.userAgent || "")
            ? "⌘K"
            : "Ctrl+K";
    return (
        <button
            type="button"
            data-testid="footer-q-btn"
            aria-label={`Qoryn Commands (${modLabel})`}
            title={`Qoryn Commands · ${modLabel}`}
            onClick={onClick}
            className="mt-auto mb-3 flex h-10 w-10 items-center justify-center rounded-md transition-all duration-200 qoryn-focus hover:bg-qoryn-cyan/[0.06] hover:shadow-[0_0_18px_rgba(0,229,255,0.14)] hover:scale-105 active:scale-95"
        >
            <img
                data-testid="footer-q-img"
                src="/qoryn-q-white.png"
                alt="Qoryn"
                width={28}
                height={28}
                draggable={false}
                style={{ width: 28, height: 28, objectFit: "contain", backgroundColor: "transparent", opacity: 0.78 }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.78"; }}
            />
        </button>
    );
}

export function NavRail({ onOpenLauncher }) {
    return (
        <nav
            data-testid="nav-rail"
            className="hidden lg:flex flex-col items-center gap-1 pt-4 pb-3 w-16 shrink-0 border-r border-qoryn-border/60 bg-black/25"
        >
            {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                    <NavLink
                        key={item.id}
                        to={item.to}
                        end={item.to === "/"}
                        data-testid={`nav-rail-${item.id}`}
                        aria-label={item.label}
                        title={item.label}
                        className={({ isActive }) =>
                            `group relative flex h-9 w-9 items-center justify-center rounded-md transition-colors duration-200 qoryn-focus ${
                                isActive
                                    ? "bg-qoryn-cyan/10 text-qoryn-cyan"
                                    : "text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04]"
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <Icon className="h-4 w-4" strokeWidth={1.75} />
                                {isActive && (
                                    <span className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r bg-qoryn-cyan" />
                                )}
                            </>
                        )}
                    </NavLink>
                );
            })}
            <FooterQButton onClick={onOpenLauncher} />
        </nav>
    );
}

export default NavRail;
